import React, { useState } from 'react';
import { FileText, ExternalLink } from 'lucide-react';
import { colors, spacing, typography, borders } from '../../tokens';

// Custom brand icons (Lucide doesn't have these)
const XIcon: React.FC<{ size?: number }> = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const DiscordIcon: React.FC<{ size?: number }> = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" />
  </svg>
);

const TelegramIcon: React.FC<{ size?: number }> = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
  </svg>
);

interface FootersectionProps {
  onNavigate?: (page: string) => void;
}

const Footersection: React.FC<FootersectionProps> = ({ onNavigate }) => {
  const currentYear = new Date().getFullYear();
  const [hoveredLink, setHoveredLink] = useState<string | null>(null);
  const [hoveredSocial, setHoveredSocial] = useState<string | null>(null);

  // Map page IDs for internal navigation
  const pageIdMap: Record<string, string> = {
    '/staking': 'stakingpage',
  };

  const handleInternalLink = (href: string, e: React.MouseEvent) => {
    if (onNavigate && pageIdMap[href]) {
      e.preventDefault();
      onNavigate(pageIdMap[href]);
    }
  };

  const footerLinks = {
    ecosystem: [
      { label: 'Staking', href: '/staking' },
      { label: 'Nodes', href: 'https://farlabs.ai/products/farai', external: true },
    ],
    resources: [
      { label: 'White Paper', href: 'https://wp.farlabs.ai/', external: true },
    ],
    community: [
      { label: 'Discord', href: 'https://discord.gg/FarLABSAI', external: true },
      { label: 'Telegram', href: 'https://t.me/FarLabsAI', external: true },
      { label: 'Twitter', href: 'https://x.com/FARLabsAI', external: true },
    ],
  };

  const socialLinks = [
    { icon: XIcon, href: 'https://x.com/FARLabsAI', label: 'X' },
    { icon: DiscordIcon, href: 'https://discord.gg/FarLABSAI', label: 'Discord' },
    { icon: TelegramIcon, href: 'https://t.me/FarLabsAI', label: 'Telegram' },
    { icon: FileText, href: 'https://wp.farlabs.ai/', label: 'Docs' },
  ];

  return (
    <footer style={{
      width: '100%',
      background: colors.background.primary.default,
      borderTop: `1px solid ${colors.border.default.default}`,
    }}>
      {/* Main Footer Content */}
      <div style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: `${spacing['2xl']} ${spacing.lg}`,
      }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
          gap: spacing.xl,
        }}>
          {/* Logo & Description */}
          <div style={{ gridColumn: 'span 1' }}>
            <img
              src="/farlogo.svg"
              alt="Farcana"
              onClick={() => onNavigate?.('stakingpage')}
              style={{
                height: '32px',
                width: 'auto',
                marginBottom: spacing.md,
                cursor: onNavigate ? 'pointer' : 'default',
              }}
            />
            <p style={{
              fontSize: typography.fontSize.sm,
              color: colors.text.muted.default,
              lineHeight: typography.lineHeight.relaxed,
              marginBottom: spacing.lg,
            }}>
              The Lattice for AI Gaming
            </p>
            {/* Social Links */}
            <div style={{
              display: 'flex',
              gap: spacing.sm,
            }}>
              {socialLinks.map((social) => {
                const IconComponent = social.icon;
                const isHovered = hoveredSocial === social.label;
                const isInternal = 'pageId' in social;
                return (
                  <a
                    key={social.label}
                    href={social.href}
                    target={social.href.startsWith('http') ? '_blank' : undefined}
                    rel={social.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                    aria-label={social.label}
                    onClick={isInternal ? (e) => handleInternalLink(social.href, e) : undefined}
                    onMouseEnter={() => setHoveredSocial(social.label)}
                    onMouseLeave={() => setHoveredSocial(null)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '36px',
                      height: '36px',
                      borderRadius: borders.component.default.radius,
                      background: isHovered ? colors.background.tertiary.default : colors.background.secondary.default,
                      color: isHovered ? colors.brand.purple.base : colors.text.secondary.default,
                      transition: 'all 0.2s',
                      border: `1px solid ${isHovered ? colors.brand.purple.base : colors.border.default.default}`,
                      cursor: 'pointer',
                    }}
                  >
                    <IconComponent size={18} />
                  </a>
                );
              })}
            </div>
          </div>

          {/* Ecosystem */}
          <div>
            <h3 style={{
              fontSize: typography.fontSize.sm,
              fontWeight: typography.fontWeight.semibold,
              color: colors.text.primary.default,
              marginBottom: spacing.md,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
            }}>
              Ecosystem
            </h3>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {footerLinks.ecosystem.map((link) => {
                const isHovered = hoveredLink === `eco-${link.label}`;
                const isExternal = 'external' in link && link.external;
                return (
                  <li key={link.label} style={{ marginBottom: spacing.sm }}>
                    <a
                      href={link.href}
                      target={isExternal ? '_blank' : undefined}
                      rel={isExternal ? 'noopener noreferrer' : undefined}
                      onClick={isExternal ? undefined : (e) => handleInternalLink(link.href, e)}
                      onMouseEnter={() => setHoveredLink(`eco-${link.label}`)}
                      onMouseLeave={() => setHoveredLink(null)}
                      style={{
                        fontSize: typography.fontSize.sm,
                        color: isHovered ? colors.brand.purple.base : colors.text.secondary.default,
                        textDecoration: 'none',
                        transition: 'color 0.2s',
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: spacing.xs,
                      }}
                    >
                      {link.label}
                      {isExternal && <ExternalLink size={12} />}
                    </a>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Resources */}
          <div>
            <h3 style={{
              fontSize: typography.fontSize.sm,
              fontWeight: typography.fontWeight.semibold,
              color: colors.text.primary.default,
              marginBottom: spacing.md,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
            }}>
              Resources
            </h3>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {footerLinks.resources.map((link) => {
                const isHovered = hoveredLink === `res-${link.label}`;
                return (
                  <li key={link.label} style={{ marginBottom: spacing.sm }}>
                    <a
                      href={link.href}
                      target={link.external ? '_blank' : undefined}
                      rel={link.external ? 'noopener noreferrer' : undefined}
                      onMouseEnter={() => setHoveredLink(`res-${link.label}`)}
                      onMouseLeave={() => setHoveredLink(null)}
                      style={{
                        fontSize: typography.fontSize.sm,
                        color: isHovered ? colors.brand.purple.base : colors.text.secondary.default,
                        textDecoration: 'none',
                        transition: 'color 0.2s',
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: spacing.xs,
                      }}
                    >
                      {link.label}
                      {link.external && <ExternalLink size={12} />}
                    </a>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Community */}
          <div>
            <h3 style={{
              fontSize: typography.fontSize.sm,
              fontWeight: typography.fontWeight.semibold,
              color: colors.text.primary.default,
              marginBottom: spacing.md,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
            }}>
              Community
            </h3>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {footerLinks.community.map((link) => {
                const isHovered = hoveredLink === `com-${link.label}`;
                return (
                  <li key={link.label} style={{ marginBottom: spacing.sm }}>
                    <a
                      href={link.href}
                      target={link.external ? '_blank' : undefined}
                      rel={link.external ? 'noopener noreferrer' : undefined}
                      onMouseEnter={() => setHoveredLink(`com-${link.label}`)}
                      onMouseLeave={() => setHoveredLink(null)}
                      style={{
                        fontSize: typography.fontSize.sm,
                        color: isHovered ? colors.brand.purple.base : colors.text.secondary.default,
                        textDecoration: 'none',
                        transition: 'color 0.2s',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: spacing.xs,
                      }}
                    >
                      {link.label}
                      {link.external && <ExternalLink size={12} />}
                    </a>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div style={{
        borderTop: `1px solid ${colors.border.default.default}`,
        background: colors.background.secondary.default,
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          padding: `${spacing.md} ${spacing.lg}`,
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: spacing.md,
        }}>
          <span style={{
            fontSize: typography.fontSize.xs,
            color: colors.text.muted.default,
          }}>
            © {currentYear} Farcana. All rights reserved.
          </span>
          <span style={{
            fontSize: typography.fontSize.xs,
            color: colors.text.muted.default,
          }}>
            Built for the future of AI gaming
          </span>
        </div>
      </div>
    </footer>
  );
};

export default Footersection;
